// Warehouse Module API & State Service
// Implements 3-tier lifecycle: Draft -> Submitted -> Completed

import apiClient from '../apiClient';

const STORAGE_KEYS = {
    LOADING_REQUESTS: 'yaseero_warehouse_loading_requests',
    OFFLOAD_REQUESTS: 'yaseero_warehouse_offload_requests',
    TRANSFER_HISTORY: 'yaseero_warehouse_transfer_history',
};

// Seed Products List
export const SEED_PRODUCTS = [
    { id: 'p1', name: 'Rice 5kg', sku: 'RICE-5KG', unit: 'Pcs', category: 'Grains' },
    { id: 'p2', name: 'Oil 1L', sku: 'OIL-1L', unit: 'Pcs', category: 'Oil' },
    { id: 'p3', name: 'Sugar 1kg', sku: 'SUGAR-1KG', unit: 'Pcs', category: 'Grains' },
    { id: 'p4', name: 'Tea 500g', sku: 'TEA-500G', unit: 'Pcs', category: 'Beverages' },
    { id: 'p5', name: 'Water Bottle 1.5L', sku: 'WATER-1.5L', unit: 'Pcs', category: 'Beverages' },
    { id: 'p6', name: 'Almarai Milk 1L', sku: 'MILK-1L', unit: 'Pcs', category: 'Dairy' },
    { id: 'p7', name: 'Nadec Juice 1L', sku: 'JUICE-1L', unit: 'Pcs', category: 'Beverages' },
];

// Seed Pending Customer Orders assigned to logged-in Salesperson
export const SEED_PENDING_ORDERS = [
    {
        orderNo: 'ORD-1021',
        customer: 'Al Madina Supermarket',
        date: '2026-07-24',
        items: [
            { productId: 'p1', productName: 'Rice 5kg', qty: 5 },
            { productId: 'p2', productName: 'Oil 1L', qty: 4 },
        ]
    },
    {
        orderNo: 'ORD-1030',
        customer: 'West Zone Fresh',
        date: '2026-07-24',
        items: [
            { productId: 'p1', productName: 'Rice 5kg', qty: 5 },
            { productId: 'p2', productName: 'Oil 1L', qty: 6 },
            { productId: 'p3', productName: 'Sugar 1kg', qty: 4 },
        ]
    },
    {
        orderNo: 'ORD-1035',
        customer: 'Baqer Mohebi Grocery',
        date: '2026-07-25',
        items: [
            { productId: 'p1', productName: 'Rice 5kg', qty: 6 },
            { productId: 'p2', productName: 'Oil 1L', qty: 5 },
            { productId: 'p3', productName: 'Sugar 1kg', qty: 6 },
        ]
    },
    {
        orderNo: 'ORD-1042',
        customer: 'Grand Hypermarket',
        date: '2026-07-25',
        items: [
            { productId: 'p1', productName: 'Rice 5kg', qty: 4 },
        ]
    }
];

const getTodayIso = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};
const getTodayFormatted = () => {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

// Initial Storage Fallbacks (Empty by default)
const INITIAL_LOADING_REQUESTS = [];
const INITIAL_OFFLOAD_REQUESTS = [];
const INITIAL_TRANSFER_HISTORY = [];

// Helper to get items from local storage with initial fallback
const getStorageData = (key, fallback) => {
    try {
        const data = localStorage.getItem(key);
        if (!data) {
            localStorage.setItem(key, JSON.stringify(fallback));
            return fallback;
        }
        return JSON.parse(data);
    } catch {
        return fallback;
    }
};

const setStorageData = (key, data) => {
    try {
        localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {
        console.error('LocalStorage write error', e);
    }
};

// API Functions

export const getWarehouseDashboardStats = async () => {
    try {
        const response = await apiClient.get('/v1/warehouse/dashboard-stats');
        return response.data;
    } catch (err) {
        // Local state fallback for offline dev mode
        const loadingReqs = getStorageData(STORAGE_KEYS.LOADING_REQUESTS, INITIAL_LOADING_REQUESTS);
        const offloadReqs = getStorageData(STORAGE_KEYS.OFFLOAD_REQUESTS, INITIAL_OFFLOAD_REQUESTS);
        const transfers = getStorageData(STORAGE_KEYS.TRANSFER_HISTORY, INITIAL_TRANSFER_HISTORY);

        const pendingLoadingList = loadingReqs.filter(r => {
            const s = (r.status || '').toUpperCase();
            return s === 'SUBMITTED' || s === 'PENDING APPROVAL' || s === 'PENDING' || s === 'DRAFT' || s === 'REVISION PENDING';
        });

        const pendingOffloadList = offloadReqs.filter(r => {
            const s = (r.status || '').toUpperCase();
            return s === 'SUBMITTED' || s === 'PENDING RETURN' || s === 'UNDER VERIFICATION' || s === 'PENDING' || s === 'DRAFT';
        });

        const completedTransfersToday = transfers.filter(t => t.status === 'Completed').length;

        return {
            totalActiveProducts: 0,
            totalStockQty: 0,
            pendingLoadingCount: pendingLoadingList.length,
            pendingOffloadCount: pendingOffloadList.length,
            completedTransfersToday: completedTransfersToday,
            recentLoading: pendingLoadingList.slice(0, 5),
            recentOffload: pendingOffloadList.slice(0, 5),
        };
    }
};

// Calculate smart order suggestions from pending customer orders
export const getSuggestedLoadingItems = () => {
    const productMap = {};

    SEED_PENDING_ORDERS.forEach(order => {
        order.items.forEach(item => {
            if (!productMap[item.productId]) {
                productMap[item.productId] = {
                    productId: item.productId,
                    productName: item.productName,
                    suggestedQty: 0,
                    requestedQty: 0,
                    warehouseQty: 0,
                    unit: 'Pcs',
                    source: '0 Orders',
                    orderRefs: []
                };
            }
            productMap[item.productId].suggestedQty += item.qty;
            productMap[item.productId].requestedQty += item.qty;
            productMap[item.productId].warehouseQty += item.qty;
            if (!productMap[item.productId].orderRefs.includes(order.orderNo)) {
                productMap[item.productId].orderRefs.push(order.orderNo);
            }
        });
    });

    return Object.values(productMap).map(item => ({
        ...item,
        source: `${item.orderRefs.length} Orders`
    }));
};

export const getRelatedOrdersForProduct = (productId) => {
    const orders = [];
    SEED_PENDING_ORDERS.forEach(ord => {
        const item = ord.items.find(i => i.productId === productId);
        if (item) {
            orders.push({
                orderNo: ord.orderNo,
                customer: ord.customer,
                date: ord.date,
                qty: item.qty,
                status: 'Pending'
            });
        }
    });
    return orders;
};

// Loading Requests API
export const getLoadingRequests = async (filters = {}) => {
    try {
        const response = await apiClient.get('/v1/warehouse/loading-requests', { params: filters });
        return response.data;
    } catch {
        let list = getStorageData(STORAGE_KEYS.LOADING_REQUESTS, INITIAL_LOADING_REQUESTS);
        if (filters.search) {
            const query = filters.search.toLowerCase();
            list = list.filter(r => r.requestNo.toLowerCase().includes(query) || r.salesperson.toLowerCase().includes(query) || r.vehicle.toLowerCase().includes(query));
        }
        if (filters.status && filters.status !== 'ALL') {
            list = list.filter(r => r.status.toUpperCase() === filters.status.toUpperCase());
        }
        return list;
    }
};

export const saveLoadingRequest = async (requestData) => {
    try {
        const response = await apiClient.post('/v1/warehouse/loading-requests', requestData);
        return response.data;
    } catch {
        const list = getStorageData(STORAGE_KEYS.LOADING_REQUESTS, INITIAL_LOADING_REQUESTS);
        let updated;
        let resultObj;

        const defaultVehicle = requestData.vehicle || 'Van 01';
        const defaultWarehouse = requestData.warehouse || 'Main Warehouse';

        if (requestData.id && list.some(r => r.id === requestData.id)) {
            updated = list.map(r => r.id === requestData.id ? {
                ...r,
                ...requestData,
                vehicle: requestData.vehicle || r.vehicle || defaultVehicle,
                warehouse: requestData.warehouse || r.warehouse || defaultWarehouse,
                totalQty: requestData.items ? requestData.items.reduce((sum, i) => sum + Number(i.requestedQty || i.qty || 0), 0) : r.totalQty,
                itemsCount: requestData.items ? requestData.items.length : r.itemsCount
            } : r);
            resultObj = updated.find(r => r.id === requestData.id);
        } else {
            let nextSeq = list.length + 1;
            let reqNo = `LR-${String(nextSeq).padStart(5, '0')}`;
            while (list.some(r => r.requestNo === reqNo)) {
                nextSeq++;
                reqNo = `LR-${String(nextSeq).padStart(5, '0')}`;
            }
            const todayIso = new Date().toISOString().split('T')[0];
            const newReq = {
                vehicle: defaultVehicle,
                warehouse: defaultWarehouse,
                ...requestData,
                id: `lr_${Date.now()}`,
                requestNo: reqNo,
                vehicle: requestData.vehicle || defaultVehicle,
                warehouse: requestData.warehouse || defaultWarehouse,
                loadingDate: requestData.loadingDate || todayIso,
                createdDateIso: todayIso,
                date: new Date().toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
                status: requestData.status || 'Submitted',
                totalQty: requestData.items ? requestData.items.reduce((sum, i) => sum + Number(i.requestedQty || i.qty || 0), 0) : 0,
                itemsCount: requestData.items ? requestData.items.length : 0
            };
            updated = [newReq, ...list];
            resultObj = newReq;
        }

        setStorageData(STORAGE_KEYS.LOADING_REQUESTS, updated);
        return resultObj;
    }
};

export const approveLoadingRequest = async (requestId, warehouseRemarks = '', staffName = 'Warehouse Staff') => {
    try {
        const response = await apiClient.post(`/v1/warehouse/loading-requests/${requestId}/approve`, { warehouseRemarks, staffName });
        return response.data;
    } catch {
        const list = getStorageData(STORAGE_KEYS.LOADING_REQUESTS, INITIAL_LOADING_REQUESTS);
        const target = list.find(r => r.id === requestId);
        if (!target) return null;

        const timestamp = new Date().toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

        const updatedReq = {
            ...target,
            status: 'Approved',
            warehouseRemarks: warehouseRemarks || target.warehouseRemarks || 'Approved for vehicle loading by warehouse staff',
            history: [
                ...(target.history || []),
                {
                    version: 1,
                    title: 'Approved',
                    timestamp,
                    user: staffName,
                    notes: warehouseRemarks || 'Approved for vehicle loading by warehouse staff'
                }
            ]
        };

        const updatedList = list.map(r => r.id === requestId ? updatedReq : r);
        setStorageData(STORAGE_KEYS.LOADING_REQUESTS, updatedList);
        return updatedReq;
    }
};

export const completeLoadingRequest = async (id, warehouseData = {}) => {
    try {
        const response = await apiClient.post(`/v1/warehouse/loading-requests/${id}/complete`, warehouseData);
        return response.data;
    } catch {
        const list = getStorageData(STORAGE_KEYS.LOADING_REQUESTS, INITIAL_LOADING_REQUESTS);
        const target = list.find(r => r.id === id);

        if (!target) return null;

        const completedReq = {
            ...target,
            status: 'Completed',
            warehouseRemarks: warehouseData.warehouseRemarks || 'Prepared & verified by warehouse staff.',
            completedBy: warehouseData.completedBy || 'Warehouse Staff',
            completionTime: new Date().toLocaleString(),
            items: target.items.map(item => ({
                ...item,
                warehouseQty: warehouseData.itemsQty?.[item.productId] ?? item.requestedQty
            }))
        };

        const updatedList = list.map(r => r.id === id ? completedReq : r);
        setStorageData(STORAGE_KEYS.LOADING_REQUESTS, updatedList);

        // Auto-create Transfer History record
        const transfers = getStorageData(STORAGE_KEYS.TRANSFER_HISTORY, INITIAL_TRANSFER_HISTORY);
        const newTransferNo = `TR-${String(transfers.length + 35).padStart(5, '0')}`;
        const newTransfer = {
            id: newTransferNo,
            transferNo: newTransferNo,
            type: 'Loading',
            requestNo: completedReq.requestNo,
            salesperson: completedReq.salesperson,
            vehicle: completedReq.vehicle,
            fromLocation: completedReq.warehouse,
            toLocation: completedReq.vehicle,
            date: completedReq.completionTime,
            itemsCount: completedReq.items.length,
            totalQty: completedReq.items.reduce((s, i) => s + Number(i.warehouseQty || i.requestedQty), 0),
            status: 'Completed',
            warehouseRemarks: completedReq.warehouseRemarks,
            completedBy: completedReq.completedBy,
            items: completedReq.items.map(i => ({
                productId: i.productId,
                productName: i.productName,
                qty: i.warehouseQty || i.requestedQty,
                unit: i.unit
            }))
        };

        setStorageData(STORAGE_KEYS.TRANSFER_HISTORY, [newTransfer, ...transfers]);
        return completedReq;
    }
};

// Offload Requests API
export const getOffloadRequests = async (filters = {}) => {
    try {
        const response = await apiClient.get('/v1/warehouse/offload-requests', { params: filters });
        return response.data;
    } catch {
        let list = getStorageData(STORAGE_KEYS.OFFLOAD_REQUESTS, INITIAL_OFFLOAD_REQUESTS);
        if (filters.search) {
            const query = filters.search.toLowerCase();
            list = list.filter(r => r.requestNo.toLowerCase().includes(query) || r.salesperson.toLowerCase().includes(query) || r.vehicle.toLowerCase().includes(query));
        }
        if (filters.status && filters.status !== 'ALL') {
            list = list.filter(r => r.status.toUpperCase() === filters.status.toUpperCase());
        }
        return list;
    }
};

export const saveOffloadRequest = async (requestData) => {
    try {
        const response = await apiClient.post('/v1/warehouse/offload-requests', requestData);
        return response.data;
    } catch {
        const list = getStorageData(STORAGE_KEYS.OFFLOAD_REQUESTS, INITIAL_OFFLOAD_REQUESTS);
        let updated;
        let resultObj;

        const defaultVehicle = requestData.vehicle || 'Van 01';
        const defaultWarehouse = requestData.warehouse || 'Main Warehouse';

        if (requestData.id && list.some(r => r.id === requestData.id)) {
            updated = list.map(r => r.id === requestData.id ? {
                ...r,
                ...requestData,
                vehicle: requestData.vehicle || r.vehicle || defaultVehicle,
                warehouse: requestData.warehouse || r.warehouse || defaultWarehouse,
                totalQty: requestData.items ? requestData.items.reduce((sum, i) => sum + Number(i.qty || 0), 0) : r.totalQty,
                itemsCount: requestData.items ? requestData.items.length : r.itemsCount
            } : r);
            resultObj = updated.find(r => r.id === requestData.id);
        } else {
            let nextSeq = list.length + 1;
            let reqNo = `OR-${String(nextSeq).padStart(5, '0')}`;
            while (list.some(r => r.requestNo === reqNo)) {
                nextSeq++;
                reqNo = `OR-${String(nextSeq).padStart(5, '0')}`;
            }
            const todayIso = new Date().toISOString().split('T')[0];
            const newReq = {
                vehicle: defaultVehicle,
                warehouse: defaultWarehouse,
                ...requestData,
                id: `or_${Date.now()}`,
                requestNo: reqNo,
                vehicle: requestData.vehicle || defaultVehicle,
                warehouse: requestData.warehouse || defaultWarehouse,
                requestDate: requestData.requestDate || todayIso,
                createdDateIso: todayIso,
                date: new Date().toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
                status: requestData.status || 'Submitted',
                totalQty: requestData.items ? requestData.items.reduce((sum, i) => sum + Number(i.qty || 0), 0) : 0,
                itemsCount: requestData.items ? requestData.items.length : 0
            };
            updated = [newReq, ...list];
            resultObj = newReq;
        }

        setStorageData(STORAGE_KEYS.OFFLOAD_REQUESTS, updated);
        return resultObj;
    }
};

export const completeOffloadRequest = async (id, warehouseData = {}) => {
    try {
        const response = await apiClient.post(`/v1/warehouse/offload-requests/${id}/complete`, warehouseData);
        return response.data;
    } catch {
        const list = getStorageData(STORAGE_KEYS.OFFLOAD_REQUESTS, INITIAL_OFFLOAD_REQUESTS);
        const target = list.find(r => r.id === id);

        if (!target) return null;

        const completedReq = {
            ...target,
            status: 'Completed',
            warehouseRemarks: warehouseData.warehouseRemarks || 'Returned stock verified and placed in warehouse.',
            completedBy: warehouseData.completedBy || 'Warehouse Staff',
            completionTime: new Date().toLocaleString(),
            items: target.items.map(item => ({
                ...item,
                warehouseQty: warehouseData.itemsQty?.[item.productId] ?? item.qty
            }))
        };

        const updatedList = list.map(r => r.id === id ? completedReq : r);
        setStorageData(STORAGE_KEYS.OFFLOAD_REQUESTS, updatedList);

        // Auto-create Transfer History record
        const transfers = getStorageData(STORAGE_KEYS.TRANSFER_HISTORY, INITIAL_TRANSFER_HISTORY);
        const newTransferNo = `TR-${String(transfers.length + 35).padStart(5, '0')}`;
        const newTransfer = {
            id: newTransferNo,
            transferNo: newTransferNo,
            type: 'Offload',
            requestNo: completedReq.requestNo,
            salesperson: completedReq.salesperson,
            vehicle: completedReq.vehicle,
            fromLocation: completedReq.vehicle,
            toLocation: completedReq.warehouse,
            date: completedReq.completionTime,
            itemsCount: completedReq.items.length,
            totalQty: completedReq.items.reduce((s, i) => s + Number(i.warehouseQty || i.qty), 0),
            status: 'Completed',
            warehouseRemarks: completedReq.warehouseRemarks,
            completedBy: completedReq.completedBy,
            items: completedReq.items.map(i => ({
                productId: i.productId,
                productName: i.productName,
                qty: i.warehouseQty || i.qty,
                unit: i.unit,
                reason: i.reason
            }))
        };

        setStorageData(STORAGE_KEYS.TRANSFER_HISTORY, [newTransfer, ...transfers]);
        return completedReq;
    }
};

// Transfer History API
export const getTransferHistory = async (filters = {}) => {
    try {
        const response = await apiClient.get('/v1/warehouse/transfer-history', { params: filters });
        return response.data;
    } catch {
        let list = getStorageData(STORAGE_KEYS.TRANSFER_HISTORY, INITIAL_TRANSFER_HISTORY);
        if (filters.search) {
            const query = filters.search.toLowerCase();
            list = list.filter(t => t.transferNo.toLowerCase().includes(query) || t.requestNo.toLowerCase().includes(query) || t.salesperson.toLowerCase().includes(query) || t.vehicle.toLowerCase().includes(query));
        }
        if (filters.type && filters.type !== 'ALL') {
            list = list.filter(t => t.type.toUpperCase() === filters.type.toUpperCase());
        }
        return list;
    }
};

export const deleteDraftRequest = async (id, type = 'loading') => {
    try {
        await apiClient.delete(`/v1/warehouse/requests/${id}`, { params: { type } });
    } catch {
        const key = type === 'loading' ? STORAGE_KEYS.LOADING_REQUESTS : STORAGE_KEYS.OFFLOAD_REQUESTS;
        const fallback = type === 'loading' ? INITIAL_LOADING_REQUESTS : INITIAL_OFFLOAD_REQUESTS;
        const list = getStorageData(key, fallback);

        const updated = list.filter(r => !(r.id === id && r.status === 'Draft'));
        setStorageData(key, updated);
        return updated;
    }
};

export const submitLoadingRevision = async (requestId, revisionData) => {
    try {
        const response = await apiClient.post(`/v1/warehouse/loading-requests/${requestId}/revision`, revisionData);
        return response.data;
    } catch {
        const list = getStorageData(STORAGE_KEYS.LOADING_REQUESTS, INITIAL_LOADING_REQUESTS);
        const target = list.find(r => r.id === requestId);
        if (!target) return null;

        const currentHistory = target.history || [
            { version: 1, title: 'Created', timestamp: target.date, user: target.salesperson, notes: 'Created Loading Request' },
            { version: 1, title: 'Approved', timestamp: target.date, user: 'Warehouse Staff', notes: 'Approved' }
        ];

        const nextVersion = (currentHistory.length > 0 ? Math.max(...currentHistory.map(h => h.version || 1)) : 1) + 1;
        const timestamp = new Date().toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

        const updatedReq = {
            ...target,
            status: 'Revision Pending',
            pendingRevision: {
                version: nextVersion,
                requestedItems: revisionData.items,
                reason: revisionData.reason,
                requestedBy: revisionData.salesperson || target.salesperson,
                requestedAt: timestamp,
            },
            history: [
                ...currentHistory,
                {
                    version: nextVersion,
                    title: 'Revision Requested',
                    timestamp,
                    user: revisionData.salesperson || target.salesperson,
                    notes: `Reason: ${revisionData.reason}`
                }
            ]
        };

        const updatedList = list.map(r => r.id === requestId ? updatedReq : r);
        setStorageData(STORAGE_KEYS.LOADING_REQUESTS, updatedList);
        return updatedReq;
    }
};

export const approveLoadingRevision = async (requestId, warehouseRemarks = '', staffName = 'Warehouse Staff') => {
    try {
        const response = await apiClient.post(`/v1/warehouse/loading-requests/${requestId}/approve-revision`, { warehouseRemarks, staffName });
        return response.data;
    } catch {
        const list = getStorageData(STORAGE_KEYS.LOADING_REQUESTS, INITIAL_LOADING_REQUESTS);
        const target = list.find(r => r.id === requestId);
        if (!target || !target.pendingRevision) return null;

        const revisedItems = target.pendingRevision.requestedItems;
        const totalQty = revisedItems.reduce((s, i) => s + Number(i.requestedQty || 0), 0);
        const timestamp = new Date().toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

        const updatedReq = {
            ...target,
            status: 'Approved (Updated)',
            isUpdated: true,
            items: revisedItems,
            totalQty,
            itemsCount: revisedItems.length,
            warehouseRemarks: warehouseRemarks || target.warehouseRemarks,
            pendingRevision: null,
            history: [
                ...(target.history || []),
                {
                    version: target.pendingRevision.version || 2,
                    title: 'Revision Approved',
                    timestamp,
                    user: staffName,
                    notes: warehouseRemarks || 'Revision approved by warehouse staff'
                }
            ]
        };

        const updatedList = list.map(r => r.id === requestId ? updatedReq : r);
        setStorageData(STORAGE_KEYS.LOADING_REQUESTS, updatedList);
        return updatedReq;
    }
};

export const rejectLoadingRevision = async (requestId, warehouseRemarks = '', staffName = 'Warehouse Staff') => {
    try {
        const response = await apiClient.post(`/v1/warehouse/loading-requests/${requestId}/reject-revision`, { warehouseRemarks, staffName });
        return response.data;
    } catch {
        const list = getStorageData(STORAGE_KEYS.LOADING_REQUESTS, INITIAL_LOADING_REQUESTS);
        const target = list.find(r => r.id === requestId);
        if (!target || !target.pendingRevision) return null;

        const timestamp = new Date().toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

        const updatedReq = {
            ...target,
            status: target.isUpdated ? 'Approved (Updated)' : 'Approved',
            pendingRevision: null,
            history: [
                ...(target.history || []),
                {
                    version: target.pendingRevision.version || 2,
                    title: 'Revision Rejected',
                    timestamp,
                    user: staffName,
                    notes: warehouseRemarks || 'Revision rejected by warehouse staff'
                }
            ]
        };

        const updatedList = list.map(r => r.id === requestId ? updatedReq : r);
        setStorageData(STORAGE_KEYS.LOADING_REQUESTS, updatedList);
        return updatedReq;
    }
};

export const cancelLoadingRevision = async (requestId, salespersonName = 'Salesperson') => {
    try {
        const response = await apiClient.post(`/v1/warehouse/loading-requests/${requestId}/cancel-revision`, { salespersonName });
        return response.data;
    } catch {
        const list = getStorageData(STORAGE_KEYS.LOADING_REQUESTS, INITIAL_LOADING_REQUESTS);
        const target = list.find(r => r.id === requestId);
        if (!target || !target.pendingRevision) return null;

        const timestamp = new Date().toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

        const updatedReq = {
            ...target,
            status: target.isUpdated ? 'Approved (Updated)' : 'Approved',
            pendingRevision: null,
            history: [
                ...(target.history || []),
                {
                    version: target.pendingRevision.version || 2,
                    title: 'Revision Cancelled',
                    timestamp,
                    user: salespersonName,
                    notes: 'Revision request cancelled by salesperson'
                }
            ]
        };

        const updatedList = list.map(r => r.id === requestId ? updatedReq : r);
        setStorageData(STORAGE_KEYS.LOADING_REQUESTS, updatedList);
        return updatedReq;
    }
};

export const rejectLoadingRequest = async (requestId, warehouseRemarks = '', staffName = 'Warehouse Staff') => {
    try {
        const response = await apiClient.post(`/v1/warehouse/loading-requests/${requestId}/reject`, { warehouseRemarks, staffName });
        return response.data;
    } catch {
        const list = getStorageData(STORAGE_KEYS.LOADING_REQUESTS, INITIAL_LOADING_REQUESTS);
        const target = list.find(r => r.id === requestId);
        if (!target) return null;

        const timestamp = new Date().toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

        const updatedReq = {
            ...target,
            status: 'Rejected',
            warehouseRemarks: warehouseRemarks || 'Request rejected by warehouse staff',
            history: [
                ...(target.history || []),
                {
                    version: 1,
                    title: 'Request Rejected',
                    timestamp,
                    user: staffName,
                    notes: warehouseRemarks || 'Request rejected by warehouse staff'
                }
            ]
        };

        const updatedList = list.map(r => r.id === requestId ? updatedReq : r);
        setStorageData(STORAGE_KEYS.LOADING_REQUESTS, updatedList);
        return updatedReq;
    }
};
